import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Linkedin, Instagram } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleNavigation = (id) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
    } else {
      scrollToSection(id);
    }
  };

  const handleHomeClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleContactClick = () => {
    navigate('/kontakt');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExportCarsClick = () => {
    navigate('/export-cars');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOurHistoryClick = () => {
    navigate('/vores-historie');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSellLeasingCarClick = () => {
    navigate('/saelg-din-bil');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer id="kontakt" className="bg-slate-50 text-slate-600 pt-12 pb-8 border-t border-slate-200/60">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 pb-10 border-b border-slate-200/60">
          
          {/* Brand Column */}
          <div className="md:col-span-4 space-y-4">
            <div className="flex items-center text-left">
              <button onClick={handleHomeClick} className="focus:outline-none cursor-pointer">
                <img 
                  src="https://assets.auxodev.dk/assets/5bfde40d-44ae-4b1e-9e89-bf8f3817c44f/ea839c94-dab5-4ca3-9c09-fd0ca187cd26.gif" 
                  alt="LeaseRetur Logo" 
                  className="h-12 md:h-14 w-auto object-contain"
                />
              </button>
            </div>
            
            <p className="text-xs text-slate-500 max-w-xs leading-relaxed">
              {t('footer.desc')}
            </p>

            <div className="flex items-center gap-4">
              <a href="https://www.facebook.com/people/Vejle-AutoExport/pfbid0KbFsG3cov4MZ77eakCu167NQJHyMbE8rA3nwSUw99KEYV5BzQvg7Fy5vN3AkL1Ddl/" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-900 transition-colors" aria-label="Facebook">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="#" className="text-slate-500 hover:text-slate-900 transition-colors" aria-label="LinkedIn">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/vejleautoexport/" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-900 transition-colors" aria-label="Instagram">
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Navigation Links Column */}
          <div className="md:col-span-3 space-y-4">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-widest">{t('footer.nav_title')}</h3>
            <ul className="space-y-2.5 text-xs font-bold uppercase tracking-wider">
              <li>
                <button onClick={handleHomeClick} className="text-slate-600 hover:text-slate-900 transition-colors text-left cursor-pointer">
                  {t('nav.home')}
                </button>
              </li>
              <li>
                <button onClick={handleSellLeasingCarClick} className="text-slate-600 hover:text-slate-900 transition-colors text-left cursor-pointer">
                  {t('nav.sell')}
                </button>
              </li>
              <li>
                <button onClick={handleExportCarsClick} className="text-slate-600 hover:text-slate-900 transition-colors text-left cursor-pointer">
                  {t('nav.export')}
                </button>
              </li>
              <li>
                <button onClick={handleOurHistoryClick} className="text-slate-600 hover:text-slate-900 transition-colors text-left cursor-pointer">
                  {t('nav.history')}
                </button>
              </li>
              <li>
                <button onClick={handleContactClick} className="text-slate-600 hover:text-slate-900 transition-colors text-left cursor-pointer">
                  {t('nav.contact')}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div className="md:col-span-5 space-y-4">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-widest">{t('footer.contact_title')}</h3>
            <ul className="space-y-3.5 text-sm">
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-[#293e30] shrink-0" />
                <a href="mailto:info@vejleautoexport.dk" className="text-slate-800 hover:text-[#293e30] transition-colors font-medium">
                  info@vejleautoexport.dk
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-[#293e30] shrink-0" />
                <a href="tel:+4560133473" className="text-slate-800 hover:text-[#293e30] transition-colors font-medium">
                  60 13 34 73
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-[#293e30] shrink-0" />
                <span className="text-slate-800 font-medium">
                  Storgaden 97, 6052 Viuf
                </span>
              </li>
            </ul>
          </div>

        </div>

        {/* Footer Bottom */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 text-xs text-slate-400">
          <div>
            <p>© {currentYear} Vejle AutoExport. {t('footer.rights')}</p>
            <p className="mt-0.5">CVR-nr: 30158628</p>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/privatlivspolitik" className="hover:text-slate-900 transition-colors">{t('footer.privacy')}</Link>
            <Link to="/handelsbetingelser" className="hover:text-slate-900 transition-colors">{t('footer.terms')}</Link>
          </div>
        </div>

      </div>
    </footer>
  );
}
